<template>
    <LoadingCard v-if="loading && !hasLoaded" />

    <div class="card" v-else>
        <div class="bg-primary align-items-center d-flex">
            <h5 class="card-header text-white">Spray Entries {{ dateRangeEndsToday ? `from the last ${dateRangeDayDiff} Days` : '' }}</h5>
            <b-pagination align="right" class="my-auto ml-auto mr-2" :total-rows="pagination.total" v-model="pagination.page"
                          :per-page="pagination.limit" />
        </div>

        <VueDatePicker
                id="date"
                :config="dateRangeConfig"
                v-model="dateRange"
                ref="dateRangePicker"
                class="form-control form-control-lg text-center bg-light"
                placeholder="Click to filter entries by spray date"
                name="date" />

        <b-table stacked="md" :items="tableData" :fields="sprayLogsTable.fields"
                 :per-page="pagination.limit" :current-page="pagination.page"
                 :sort-by.sync="sprayLogsTable.sortBy" :sort-desc.sync="sprayLogsTable.sortDesc"
                 :show-empty="true">

            <template slot="grower" slot-scope="row">
                <router-link class="text-nowrap" :to="{name: 'growers.blocks.browse', params: {growerId: row.item.block.owner.id}}">
                    {{ row.item.block.owner.name }}
                </router-link>
            </template>

            <template slot="variety" slot-scope="row">
                <span class="text-nowrap">{{ row.item.block.variety.name }}</span>
            </template>

            <template slot="block" slot-scope="row">
                <router-link class="text-nowrap" :to="{name: 'spray-diary.view', params: {blockId: row.item.block.id}}">
                    {{ row.item.block.name }}
                </router-link>
            </template>

            <template slot="applicator" slot-scope="row">
                {{ row.item.growsafe_number }}
            </template>

            <template slot="sprayed_at" slot-scope="row">
                <span class="text-nowrap">{{ row.item.sprayed_at | datetime_format }}</span>
            </template>

            <template slot="chemicals" slot-scope="row">
                    <span v-for="entry in [...row.item.entries].sort((a,b) => a.chemical.name.localeCompare(b.chemical.name))"
                          class="badge badge-light mr-1 mb-1">
                        {{ entry.chemical.name }}
                    </span>
            </template>

            <template slot="targets" slot-scope="row">
                    <span v-for="entry in [...row.item.entries].sort((a,b) => a.target.name.localeCompare(b.target.name))"
                          class="badge badge-light mr-1 mb-1">
                        {{ entry.target.name }}
                    </span>
            </template>

            <template slot="restrictedEntryInterval" slot-scope="row">
                <strong class="text-danger" v-if="row.item.restrictedEntryInterval">
                    {{ row.item.restrictedEntryInterval.ends_at | date_format }}
                </strong>
                <span class="text-muted" v-else>(none)</span>
            </template>

            <template slot="applicator" slot-scope="row">
                {{ row.item.volume | number_format }}
            </template>

            <template slot="fertilisers" slot-scope="row">
                <template v-for="entry in [...row.item.fertiliserEntries].sort((a,b) => a.fertiliser.name.localeCompare(b.fertiliser.name))">
                    <span class="badge badge-light mr-1 mb-1">{{ entry.fertiliser.name }}</span>
                </template>
            </template>
        </b-table>
    </div>

</template>

<script>
import listWindDirectionValuesQuery from '@gql/enumValues/queryWindDirectionEnumValues.graphql';
import listWindSpeedValuesQuery from '@gql/enumValues/queryWindSpeedEnumValues.graphql';
import listSprayLogsQuery from '@views/SprayDiary/Browse/queryListSprayLogs.graphql';
import currentUserQuery from '@views/SprayDiary/View/queryCurrentUser.graphql';
import {differenceInDays, format as dateFormat, isToday, subDays} from 'date-fns';

export default {
  name: 'BrowseSprayDiary',
  props: ['startRange'],
  apollo: {
    currentUser: {
      query: currentUserQuery,
      update: data => data.currentUser,
    },
    sprayLogs: {
      query: listSprayLogsQuery,
      variables() {
        return {
          input: {
            sprayed_at_gte: this.startDate,
            sprayed_at_lte: this.endDate,
          },
        };
      },
      manual: true,
      result({ data, loading }) {
        if (!loading) {
          this.sprayLogs = data.filterSprayLogs.data;
          this.pagination.total = data.filterSprayLogs.meta.total;
        }
      },
    },
    windSpeedValues: {
      query: listWindSpeedValuesQuery,
      update: data => data.__type.enumValues,
    },
    windDirectionValues: {
      query: listWindDirectionValuesQuery,
      update: data => data.__type.enumValues,
    },
  },
  watch: {
    loading(newValue) {
      if (!this.hasLoaded && newValue === 0) {
        this.hasLoaded = true;
      }
    },
  },
  data() {
    const end = new Date();
    const start = subDays(end, 10);
    const tenDayRange = `${dateFormat(start, 'YYYY-MM-DD')} to ${dateFormat(end, 'YYYY-MM-DD')}`;

    return {
      loading: 0,
      hasLoaded: false,
      currentUser: null,
      dateRange: this.startRange || tenDayRange,
      sprayLogs: [],
      pagination: {
        page: 1,
        limit: this.$tableDefaultSmallPageSize,
        total: 0,
      },
      windDirectionValues: [],
      windSpeedValues: [],
      dateRangeConfig: {
        mode: 'range',
        altInput: true,
        enableTime: false,
        nextArrow: '<i class="zmdi zmdi-long-arrow-right" />',
        prevArrow: '<i class="zmdi zmdi-long-arrow-left" />',
        altFormat: 'M j, Y',
        dateFormat: 'Y-m-d',
      },
      sprayLogsTable: {
        sortBy: 'sprayed_at',
        sortDesc: false,
        fields: [
          { key: 'grower', label: 'Grower' },
          { key: 'block', label: 'Block' },
          { key: 'variety', label: 'Variety', sortable: true },
          { key: 'sprayed_at', label: 'Spray Date', sortable: true },
          { key: 'chemicals', label: 'Chemicals Used' },
          { key: 'volume', label: 'Spray Volume (lt/hec)' },
          // { key: 'fertilisers', label: 'Fertilisers Used' },
          { key: 'restrictedEntryInterval', label: 'REI' },

          { key: 'targets', label: 'Targets' },
          { key: 'expand', label: ' ' },
        ],
      },
      entriesTable: {
        fields: [
          { key: 'chemical', label: 'Chemical' },
          { key: 'rate', label: 'Rate(mls/gms per 100 lts)' },
          { key: 'weight', label: 'Weight(kgs or lts)' },
        ],
      },
      fertiliserEntriesTable: {
        fields: [
          { key: 'fertiliser', label: 'Fertiliser' },
          { key: 'rate', label: 'Rate(mls/gms per 100 lts)' },
          { key: 'weight', label: 'Weight(kgs or lts)' },
        ],
      },
    };
  },
  computed: {
    tableData() {
      return this.sprayLogs.map(item => ({ ...item, _showDetails: false }));
    },
    startDate() {
      if (!this.dateRange) {
        return null;
      }

      return `${this.dateRange.split(' to ')[0]} 00:00:00`;
    },
    endDate() {
      if (!this.dateRange || !this.dateRange.includes(' to ')) {
        return `${this.dateRange.split(' to ')[0]} 23:59:59`;
      }

      return `${this.dateRange.split(' to ')[1]} 23:59:59`;
    },
    hasDateRange() {
      return this.startDate && this.endDate;
    },
    dateRangeDayDiff() {
      return differenceInDays(this.endDate, this.startDate);
    },
    dateRangeEndsToday() {
      return isToday(this.endDate);
    },
  },
  methods: {},
};
</script>
